/* © 2017-2026 Booz Allen Hamilton Inc. All Rights Reserved. */

import { gnoment } from 'shared-ui/gnoment';
import first from 'lodash/first';
import get from 'lodash/get';
import isEmpty from 'lodash/isEmpty';
import last from 'lodash/last';
import axios from 'axios';
import download from 'downloadjs';
import { SecurityHelper } from 'sarsaparilla';
import { EASTERN_NECK_ID } from 'ui-permit/src/constants';
import { point } from 'shared-ui/maps/turfUtils';

import { PRICE_MAGNITUDE } from '../constants';
import { apiHelpers } from './api-helpers';
import type { MapAsset, TreePermitData } from '../../types/permit';

export const getClassName = (error: any, displayError: boolean) => {
    if (error && displayError) {
        return 'rec-error';
    }
    return '';
};

export const getStartAndEndDates = (dates: string[]) =>
    [first(dates), last(dates)].map((d, i) =>
        d && /\//.test(d) ? (i ? last : first)(d.split('/')) : d
    );

// Disable sales just for Eastern Neck until August 1st at 10AM ET (on Test and Prod).
export const isSalesDisabled = (permitId: string) =>
    permitId === EASTERN_NECK_ID &&
    /(recreation.gov)$/.test(window.location.host) &&
    gnoment('2019-08-01T10:00:00-04:00').isAfter();

const convertAssetToFeature = (asset: Partial<MapAsset>) => {
    const longitude = asset.longitude && parseFloat(asset.longitude);
    const latitude = asset.latitude && parseFloat(asset.latitude);

    if (Number.isNaN(longitude) || Number.isNaN(latitude)) return null;
    const pointProps = {
        name: asset.name,
        featureId: asset.featureId,
        icon: asset.mapIcon,
        location: asset.location,
    };

    const feature = longitude && latitude && point([longitude, latitude], pointProps);
    return feature;
};

export const convertSearchResultToGeoJson = (
    searchResultList: Array<Partial<TreePermitData>> = []
) => {
    const geoJSON: {
        type: string;
        features: ReturnType<typeof convertAssetToFeature>[];
    } = {
        type: 'FeatureCollection',
        features: [],
    };

    searchResultList.forEach((location) => {
        if (location.lat_long && location.lat_long.long && location.lat_long.lat) {
            const asset: Partial<MapAsset> = {
                attributes: [],
                name: location.name,
                longitude: location.lat_long.long,
                latitude: location.lat_long.lat,
                featureId: location.id,
            };

            if (location.city && location.state) {
                asset.location = `${location.city}, ${location.state}`;
            } else if (location.city) {
                asset.location = location.city;
            } else if (location.state) {
                asset.location = location.state;
            }

            const feature = convertAssetToFeature(asset);
            geoJSON.features.push(feature);
        }
    });

    return geoJSON;
};

export const isFirefox = () => navigator.userAgent.toLowerCase().indexOf('firefox') > -1;

export const formatPrice = (value = 0) => `$${(value / PRICE_MAGNITUDE).toFixed(2)}`;

export const isMobile = {
    Android() {
        return window.navigator.userAgent.match(/Android/i);
    },
    BlackBerry() {
        return window.navigator.userAgent.match(/BlackBerry/i);
    },
    iOS() {
        return window.navigator.userAgent.match(/iPhone|iPad|iPod/i);
    },
    Opera() {
        return window.navigator.userAgent.match(/Opera Mini/i);
    },
    Windows() {
        return window.navigator.userAgent.match(/IEMobile/i);
    },
    any() {
        return (
            isMobile.Android() ||
            isMobile.BlackBerry() ||
            isMobile.iOS() ||
            isMobile.Opera() ||
            isMobile.Windows()
        );
    },
};

export const printBlob = (blob: Blob, fileName = 'file.pdf'): Window | null | void => {
    if (window.navigator && (window.navigator as any).msSaveOrOpenBlob) {
        // IE opens blobs this way:
        if (fileName) {
            (window.navigator as any).msSaveOrOpenBlob(blob, fileName);
        } else {
            (window.navigator as any).msSaveOrOpenBlob(blob);
        }
    } else {
        // Other browsers use iframe printing:
        const url = window.URL.createObjectURL(blob);

        if (isMobile.any()) {
            return window.open(url, '_blank');
        }

        if (isFirefox()) {
            const downloadLink = document.createElement('a');
            downloadLink.target = '_blank';
            downloadLink.download = fileName;

            downloadLink.href = url;
            downloadLink.style.display = 'none';

            document.body.append(downloadLink);

            downloadLink.click();

            document.body.removeChild(downloadLink);
            window.URL.revokeObjectURL(url);
        } else {
            const iframe = document.createElement('iframe');
            iframe.style.display = 'none';
            document.body.appendChild(iframe);

            iframe.addEventListener(
                'load',
                () => {
                    setTimeout(() => {
                        iframe.contentWindow?.focus();
                        iframe.contentWindow?.print();
                    }, 0);
                },
                false
            );

            iframe.setAttribute('src', url);
        }
    }
};

const printPDFUrl = (pdfUrl: string) => {
    if (!pdfUrl) return;
    axios
        .get(pdfUrl.replace(/\/pdf\b/, '/print'), {
            headers: { Authorization: SecurityHelper.getAuthHeader() },
            responseType: 'arraybuffer',
        })
        .then((response) =>
            printBlob(new Blob([response.data], { type: 'application/pdf' }))
        );
};

export const printTreePermit = ({
    downloadUrl,
    reservationId,
}: {
    downloadUrl: string;
    reservationId: string;
}) => {
    if (!downloadUrl && !reservationId) return;

    if (downloadUrl) {
        printPDFUrl(downloadUrl);
    } else if (process.env.API) {
        axios
            .get(`${process.env.API}/treepermit/permit/my-permits`, {
                headers: { Authorization: SecurityHelper.getAuthHeader() },
            })
            .then(({ data: { permits } }) => {
                const permit = permits.find(
                    ({ id }: { id: string }) => id === reservationId
                );
                if (permit) {
                    printPDFUrl(permit.download_url);
                }
            });
    }
};

export const initialReservation = {
    account_id: null,
    order_id: null,
    facility_id: null,
    permit_type_id: null,
    price: 0,
    clin: 0,
    sku: '',
    quantity: 0,
    point_of_sale: 'web',
    status: 'pending',
    created_at: null,
    updated_at: null,
    is_pending: true,
    holder_info: {
        first_name: null,
        last_name: null,
        email: null,
    },
    trees_number: null,
    ekopass: null,
    has_price: true,
    fees: null,
    total: null,
};

const { getWithAuth } = apiHelpers(SecurityHelper);

export const downloadTreePermitPdf = (downloadUrl: string, location: any) =>
    getWithAuth(downloadUrl, { responseType: 'arraybuffer' })
        .then((response) => {
            const filename = location.replace(/\s/g, '-');
            const content = response.headers['content-type'];
            download(response.data, `${filename}.pdf`, content);
        })
        .catch((error) => console.log(error));

export const onError = (error: { message?: string }, defaultErr: string) => {
    const errorStr: string = get(error, 'message', '');
    const errorDataStr = errorStr?.split(' ')?.filter((item: string) => {
        const val = item.substring(1);
        if (val !== val.toLowerCase()) {
            return true;
        }
        // eslint-disable-next-line
        if (val.match(/[^a-zA-Z0-9\.,:]/)) {
            return true;
        }
        return false;
    });

    const isReadableError = isEmpty(errorDataStr);

    if (isReadableError) {
        return errorStr;
    }
    return defaultErr;
};
